import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { teamPageTabs } from './components/teamPageConfig';
import { getTeamPageTabs } from './components/teamPageConfig';
import { getCurrentBoosterQueryKey } from '@/hooks/useCurrentBooster';
import { useCurrentMatchdayWindow } from '@/hooks/useCurrentMatchday';
import { useFetchTeam, getFetchTeamQueryKey } from '@/hooks/useFetchTeam';
import { getSubstitutionEligibilityQueryKey } from '@/hooks/useSubstitutionEligibility';
import { getSubstitutionHistoryQueryKey } from '@/hooks/useSubstitutionHistory';
import { getUserBoostersQueryKey } from '@/hooks/useUserBoosters';
import {
  getCurrentBooster,
  getSubstitutionEligibility,
  getSubstitutionHistory,
  getUserBoosters,
} from '@/lib/network';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/AuthStore';
import { usePlayersStore } from '@/stores/PlayersStore';
import { useUserTeamStore } from '@/stores/UserTeamStore';
import { useTeamCreateTournamentCalendarId } from '@/hooks/useTeamCreateTournamentCalendarId';
import { useViewedTeamParams } from '@/hooks/useViewedTeamParams';
import { useFetchViewedTeam } from '@/hooks/useFetchViewedTeam';
import { useIsGameweekLocked } from '@/hooks/useIsGameweekLocked';
import { useAvailableGameweeks } from '@/hooks/teams/useAwaliableGameweeks';
import { periodAxisFromFormat } from '@/lib/periodLabels';
import { buildTeamHydrationState, buildTeamHydrationVersion } from './teamHydration';
import { TeamPageLoadingSkeleton } from './components/TeamPageLoadingSkeleton';
import { TeamUpdatingModal, useTeamUpdatingWindow } from './components/TeamUpdatingModal';
import { useGamingHubModalOpen } from '@/hooks/useGamingHubModalOpen';
import { useAppSettings } from '@/hooks/useAppSettings';

const ManageTeamContainer = lazy(() =>
  import('./components/ManageTeamContainer').then(module => ({
    default: module.ManageTeamContainer,
  }))
);

const CurrentMatchdayContainer = lazy(() =>
  import('./components/CurrentMatchdayContainer').then(module => ({
    default: module.CurrentMatchdayContainer,
  }))
);

const ViewedTeamContainer = lazy(() =>
  import('./components/ViewedTeamContainer').then(module => ({
    default: module.ViewedTeamContainer,
  }))
);

const preloadManageTeamContainer = () => import('./components/ManageTeamContainer');
const preloadCurrentMatchdayContainer = () => import('./components/CurrentMatchdayContainer');

export const MyTeamPage = () => {
  const queryClient = useQueryClient();
  const {
    id: TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    format: tournamentFormat,
    hasResolved: hasResolvedTournamentCalendar,
  } = useTeamCreateTournamentCalendarId();
  const sessionState = useAuthStore(s => s.sessionState);
  const userId = useAuthStore(s => s.userId);
  const initPlayers = usePlayersStore(s => s.init);
  const players = usePlayersStore(s => s.players);
  const isPlayersInitialized = usePlayersStore(s => s.isInitialized);
  const isPlayersLoading = usePlayersStore(s => s.isLoading);
  const hydrateFromBackendTeam = useUserTeamStore(s => s.hydrateFromBackendTeam);
  const setBackendTeamId = useUserTeamStore(s => s.setBackendTeamId);
  const setCurrentMatchdayHasStarted = useUserTeamStore(s => s.setCurrentMatchdayHasStarted);
  const clearPersistedTeamData = useUserTeamStore(s => s.clearPersistedTeamData);
  const selectedTabId = useUserTeamStore(s => s.selectedTabId);
  const setSelectedTabId = useUserTeamStore(s => s.setSelectedTabId);
  const hydratedTeamVersionRef = useRef<string | null>(null);
  const hasClearedMissingTeamRef = useRef(false);
  const boosterPrefetchKeysRef = useRef<Set<string>>(new Set());
  const substitutionPrefetchKeysRef = useRef<Set<string>>(new Set());
  const [isActiveContainerChunkReady, setIsActiveContainerChunkReady] = useState(false);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const isAuthenticated = sessionState === 'authenticated';

  const { isViewMode, teamId: viewedTeamId, gameweekId: viewedGameweekId } = useViewedTeamParams();
  const viewedTeamQuery = useFetchViewedTeam(
    isViewMode ? viewedTeamId : undefined,
    isViewMode ? viewedGameweekId : undefined
  );

  const existingTeamQuery = useFetchTeam(userId ?? undefined, TEAM_CREATE_TOURNAMENT_CALENDAR_ID, {
    enabled: hasResolvedTournamentCalendar && isAuthenticated && !!userId,
  });

  const currentMatchdayQuery = useCurrentMatchdayWindow(TEAM_CREATE_TOURNAMENT_CALENDAR_ID, {
    enabled:
      hasResolvedTournamentCalendar && isAuthenticated && !!TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    source: 'team',
    userId: userId ?? undefined,
    format:
      tournamentFormat === 'classic_season_long' || tournamentFormat === 'knockout'
        ? tournamentFormat
        : undefined,
  });
  const existingTeam = existingTeamQuery.data;
  const existingTeamPlayers = useMemo(() => existingTeam?.players ?? [], [existingTeam?.players]);
  const publicCurrentMatchday = currentMatchdayQuery.data?.currentMatchday ?? null;
  const publicCurrentGameweek = currentMatchdayQuery.data?.currentGameweek ?? null;
  const publicNextMatchday = currentMatchdayQuery.data?.nextMatchday ?? null;
  const publicNextGameweek = currentMatchdayQuery.data?.nextGameweek ?? null;
  const isGameweekFormat = periodAxisFromFormat(tournamentFormat) === 'gameweek';
  const effectiveCurrentMatchday = publicCurrentMatchday;
  const isCurrentMatchdayLockedForTeam = existingTeam?.currentMatchday?.isLockedForTeam ?? false;

  const currentMatchdayStatus =
    effectiveCurrentMatchday?.status ?? existingTeam?.currentMatchday?.status;
  const effectiveCurrentMatchdayStatus = currentMatchdayStatus;
  const isLockedMatchday =
    effectiveCurrentMatchdayStatus === 'locked' ||
    effectiveCurrentMatchdayStatus === 'completed' ||
    (effectiveCurrentMatchday?.locksAt
      ? new Date() >= new Date(effectiveCurrentMatchday.locksAt)
      : false);
  const isActiveOrUpcomingMatchday =
    (effectiveCurrentMatchdayStatus === 'active' ||
      effectiveCurrentMatchdayStatus === 'upcoming') &&
    !isLockedMatchday;
  const currentGameweekStatus = publicCurrentGameweek?.status;
  // Mirror the matchday branch: fall back to the deadline time so the page
  // flips to the locked view in the sub-minute window before the backend cron
  // moves the gameweek status (and keeps working if the cron is delayed).
  const isLockedGameweek =
    currentGameweekStatus === 'locked' ||
    currentGameweekStatus === 'completed' ||
    (publicCurrentGameweek?.locksAt
      ? new Date() >= new Date(publicCurrentGameweek.locksAt)
      : false);
  const isActiveOrUpcomingGameweek =
    (currentGameweekStatus === 'active' || currentGameweekStatus === 'upcoming') &&
    !isLockedGameweek;

  const playerType = periodAxisFromFormat(tournamentFormat);

  useEffect(() => {
    if (!hasResolvedTournamentCalendar || !TEAM_CREATE_TOURNAMENT_CALENDAR_ID) {
      return;
    }

    void initPlayers(playerType, TEAM_CREATE_TOURNAMENT_CALENDAR_ID, userId, 3);
  }, [
    hasResolvedTournamentCalendar,
    initPlayers,
    playerType,
    TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    userId,
  ]);

  useEffect(() => {
    if (!existingTeam) {
      return;
    }

    setBackendTeamId(existingTeam.id);
    const hasStarted =
      isCurrentMatchdayLockedForTeam ||
      (effectiveCurrentMatchdayStatus !== 'active' &&
        effectiveCurrentMatchdayStatus !== 'upcoming');
    setCurrentMatchdayHasStarted(Boolean(hasStarted));
  }, [
    effectiveCurrentMatchdayStatus,
    existingTeam,
    isCurrentMatchdayLockedForTeam,
    setBackendTeamId,
    setCurrentMatchdayHasStarted,
  ]);

  useEffect(() => {
    if (!isAuthenticated || existingTeamQuery.isPending) return;

    if (existingTeam) {
      hasClearedMissingTeamRef.current = false;
      return;
    }

    if (!hasClearedMissingTeamRef.current) {
      clearPersistedTeamData();
      hasClearedMissingTeamRef.current = true;
    }
  }, [clearPersistedTeamData, existingTeam, existingTeamQuery.isPending, isAuthenticated]);

  useEffect(() => {
    if (!existingTeam) return;
    if (!isPlayersInitialized || players.length === 0) return;

    const teamVersion = buildTeamHydrationVersion(existingTeam);
    if (hydratedTeamVersionRef.current === teamVersion) {
      return;
    }

    hydrateFromBackendTeam(buildTeamHydrationState(existingTeam, players));
    hydratedTeamVersionRef.current = teamVersion;
  }, [existingTeam, existingTeamPlayers, hydrateFromBackendTeam, players, isPlayersInitialized]);

  // A team created after this gameweek's deadline has nextGameweek forced to null by
  // the backend's onboarding window (its "next" gameweek is treated as the team's own
  // fresh start, not an editable manage-mode target) - that's not the same as this
  // actually being the season's last gameweek, so isOnboardingWindow must rule it out.
  const isLastGameweek =
    isGameweekFormat && !publicNextGameweek && !currentMatchdayQuery.data?.isOnboardingWindow;

  const tabs = useMemo(() => {
    return getTeamPageTabs(
      isGameweekFormat
        ? publicCurrentGameweek?.number
          ? `GW${publicCurrentGameweek.number} Points`
          : 'Current Gameweek'
        : publicCurrentMatchday?.number
          ? `MD${publicCurrentMatchday.number} Points`
          : 'Current Matchday',
      isGameweekFormat
        ? publicNextGameweek?.number
          ? `Manage GW${publicNextGameweek.number}`
          : 'Next Gameweek'
        : publicNextMatchday?.number
          ? `Manage MD${publicNextMatchday.number}`
          : 'Next Matchday',
      isLastGameweek
    );
  }, [
    isGameweekFormat,
    publicCurrentGameweek?.number,
    publicCurrentMatchday?.number,
    publicNextGameweek?.number,
    publicNextMatchday?.number,
    isLastGameweek,
  ]);

  useEffect(() => {
    if (isLastGameweek && selectedTabId !== '1') {
      setSelectedTabId('1');
    }
  }, [isLastGameweek, selectedTabId, setSelectedTabId]);

  const manageTabId = teamPageTabs[1]?.id ?? '2';
  const currentMatchdayId = effectiveCurrentMatchday?.id ?? undefined;
  const nextMatchdayId = currentMatchdayQuery.data?.nextMatchday?.id ?? undefined;
  const currentGameweekId = publicCurrentGameweek?.id ?? undefined;
  const managedGameweekTransferSummary =
    isGameweekFormat && existingTeam?.managedTransferSummary?.periodType === 'gameweek'
      ? existingTeam.managedTransferSummary
      : undefined;
  const transferMatchdayId = isGameweekFormat
    ? undefined
    : isActiveOrUpcomingMatchday
      ? effectiveCurrentMatchday?.id
      : isLockedMatchday
        ? nextMatchdayId
        : undefined;
  const transferMode = isGameweekFormat
    ? managedGameweekTransferSummary?.mode
    : isActiveOrUpcomingMatchday
      ? 'immediate'
      : isLockedMatchday && nextMatchdayId
        ? 'pending'
        : undefined;

  useEffect(() => {
    if (!isAuthenticated || !userId || isViewMode) {
      return;
    }

    const tournamentCalendarId = TEAM_CREATE_TOURNAMENT_CALENDAR_ID;
    const periodIds = isGameweekFormat
      ? [...new Set([publicCurrentGameweek?.id, publicNextGameweek?.id])].filter(
          (gameweekId): gameweekId is string => Boolean(gameweekId)
        )
      : [...new Set([currentMatchdayId, nextMatchdayId])].filter(
          (matchdayId): matchdayId is string => Boolean(matchdayId)
        );

    if (!periodIds.length) {
      return;
    }

    for (const periodId of periodIds) {
      const prefetchKey = `${tournamentCalendarId}:${isGameweekFormat ? 'gw' : 'md'}:${periodId}`;
      if (boosterPrefetchKeysRef.current.has(prefetchKey)) {
        continue;
      }
      boosterPrefetchKeysRef.current.add(prefetchKey);

      void queryClient.prefetchQuery({
        queryKey: getUserBoostersQueryKey(
          tournamentCalendarId,
          isGameweekFormat ? undefined : periodId,
          isGameweekFormat ? periodId : undefined
        ),
        queryFn: () =>
          getUserBoosters({
            tournament_calendar_id: tournamentCalendarId,
            matchday_id: isGameweekFormat ? undefined : periodId,
            gameweek_id: isGameweekFormat ? periodId : undefined,
          }),
        staleTime: 30 * 1000,
      });

      void queryClient.prefetchQuery({
        queryKey: getCurrentBoosterQueryKey(
          tournamentCalendarId,
          isGameweekFormat ? undefined : periodId,
          isGameweekFormat ? periodId : undefined
        ),
        queryFn: () =>
          getCurrentBooster({
            tournament_calendar_id: tournamentCalendarId,
            matchday_id: isGameweekFormat ? undefined : periodId,
            gameweek_id: isGameweekFormat ? periodId : undefined,
          }),
        staleTime: 30 * 1000,
      });
    }
  }, [
    currentMatchdayId,
    isGameweekFormat,
    nextMatchdayId,
    publicCurrentGameweek?.id,
    publicNextGameweek?.id,
    queryClient,
    TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    userId,
    isAuthenticated,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !userId || !existingTeam?.id) {
      return;
    }
    if (isGameweekFormat ? isActiveOrUpcomingGameweek : isActiveOrUpcomingMatchday) {
      return;
    }

    const teamId = existingTeam.id;
    const periodIds = isGameweekFormat
      ? [...new Set([publicCurrentGameweek?.id, publicNextGameweek?.id])].filter(
          (gameweekId): gameweekId is string => Boolean(gameweekId)
        )
      : [...new Set([currentMatchdayId, nextMatchdayId])].filter(
          (matchdayId): matchdayId is string => Boolean(matchdayId)
        );

    const historyPrefetchKey = `${teamId}:history`;
    if (!substitutionPrefetchKeysRef.current.has(historyPrefetchKey)) {
      substitutionPrefetchKeysRef.current.add(historyPrefetchKey);
      void queryClient.prefetchQuery({
        queryKey: getSubstitutionHistoryQueryKey(teamId),
        queryFn: () => getSubstitutionHistory({ teamId }),
        staleTime: 30 * 1000,
      });
    }

    for (const periodId of periodIds) {
      const eligibilityPrefetchKey = `${teamId}:${isGameweekFormat ? 'gw' : 'md'}:${periodId}:eligibility`;
      if (substitutionPrefetchKeysRef.current.has(eligibilityPrefetchKey)) {
        continue;
      }
      substitutionPrefetchKeysRef.current.add(eligibilityPrefetchKey);

      void queryClient.prefetchQuery({
        queryKey: getSubstitutionEligibilityQueryKey(
          teamId,
          isGameweekFormat ? undefined : periodId,
          isGameweekFormat ? periodId : undefined
        ),
        queryFn: () =>
          getSubstitutionEligibility({
            teamId,
            matchdayId: isGameweekFormat ? undefined : periodId,
            gameweekId: isGameweekFormat ? periodId : undefined,
          }),
        staleTime: 30 * 1000,
      });
    }
  }, [
    currentMatchdayId,
    currentGameweekId,
    existingTeam?.id,
    isActiveOrUpcomingMatchday,
    isActiveOrUpcomingGameweek,
    isGameweekFormat,
    nextMatchdayId,
    publicCurrentGameweek?.id,
    publicNextGameweek?.id,
    queryClient,
    userId,
    isAuthenticated,
  ]);

  const isManageTabSelected = selectedTabId === manageTabId;
  const isActiveOrUpcomingPeriod = isGameweekFormat
    ? isActiveOrUpcomingGameweek
    : isActiveOrUpcomingMatchday;
  const shouldUseManageContainer =
    isActiveOrUpcomingPeriod || (isManageTabSelected && !isLastGameweek);
  const shouldShowTabs = !isActiveOrUpcomingPeriod;
  const displayMatchdayMeta =
    shouldUseManageContainer && shouldShowTabs
      ? (currentMatchdayQuery.data?.nextMatchday ?? undefined)
      : publicCurrentMatchday;
  const displayGameweekMeta =
    shouldUseManageContainer && shouldShowTabs
      ? (currentMatchdayQuery.data?.nextGameweek ?? undefined)
      : publicCurrentGameweek;

  useEffect(() => {
    let isMounted = true;
    if (!hasBootstrapped) {
      setIsActiveContainerChunkReady(false);
    }

    const loadActiveContainer = async () => {
      if (shouldUseManageContainer) {
        await preloadManageTeamContainer();
      } else {
        await preloadCurrentMatchdayContainer();
      }

      if (isMounted) {
        setIsActiveContainerChunkReady(true);
      }
    };

    void loadActiveContainer();

    return () => {
      isMounted = false;
    };
  }, [hasBootstrapped, shouldUseManageContainer]);

  const canBootstrap =
    hasResolvedTournamentCalendar &&
    isAuthenticated &&
    !!userId &&
    !existingTeamQuery.isPending &&
    !currentMatchdayQuery.isPending &&
    (isPlayersInitialized || !isPlayersLoading) &&
    isActiveContainerChunkReady &&
    (!isViewMode || !viewedTeamQuery.isLoading);

  useEffect(() => {
    if (canBootstrap) {
      setHasBootstrapped(true);
    }
  }, [canBootstrap]);

  useEffect(() => {
    if (!isAuthenticated || !userId || !existingTeam) {
      return;
    }

    const preload = () => {
      if (shouldUseManageContainer) {
        void preloadCurrentMatchdayContainer();
      } else {
        void preloadManageTeamContainer();
      }
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(() => preload(), { timeout: 1200 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(() => preload(), 300);
    return () => window.clearTimeout(timeoutId);
  }, [existingTeam, isAuthenticated, shouldUseManageContainer, userId]);

  const viewedTeamAvailableGameweeksQuery = useAvailableGameweeks(
    TEAM_CREATE_TOURNAMENT_CALENDAR_ID,
    isViewMode ? viewedTeamId : undefined,
    isViewMode ? true : undefined
  );

  const selectedGameweekMeta = useMemo(() => {
    if (!viewedGameweekId) return undefined;
    const gameweeks = viewedTeamAvailableGameweeksQuery.data?.gameweeks ?? [];
    return gameweeks.find(gw => gw.id === viewedGameweekId);
  }, [viewedTeamAvailableGameweeksQuery.data?.gameweeks, viewedGameweekId]);

  const isSelectedGameweekLocked = useIsGameweekLocked({
    status: selectedGameweekMeta?.status ?? publicCurrentGameweek?.status ?? null,
    locksAt: selectedGameweekMeta?.startsAt ?? publicCurrentGameweek?.locksAt ?? null,
  });

  // Once the gameweek's deadline has passed the team is being settled - tell
  // the user rather than letting them stare at a half-updated side. There is no
  // end time on this: the `enable_team_transition_popup` app setting is what
  // closes it, so an admin turns the pop-up off when the transition is done.
  // Dismissal is remembered per gameweek so it does not pop back on every
  // navigation, and resets when the next gameweek locks.
  const navigate = useNavigate();
  const isGamingHubModalOpen = useGamingHubModalOpen();
  const { settings: appSettings } = useAppSettings();
  const { updatingWindow, isUpdating } = useTeamUpdatingWindow(currentMatchdayQuery.data);
  const [dismissedUpdatingWindowKey, setDismissedUpdatingWindowKey] = useState<string | null>(null);
  const isTeamUpdatingModalOpen =
    appSettings.teamTransitionPopupEnabled &&
    isUpdating &&
    !isViewMode &&
    !isGamingHubModalOpen &&
    dismissedUpdatingWindowKey !== updatingWindow?.key;

  const wasTeamUpdatingModalOpenRef = useRef(isTeamUpdatingModalOpen);
  useEffect(() => {
    const wasOpen = wasTeamUpdatingModalOpenRef.current;
    wasTeamUpdatingModalOpenRef.current = isTeamUpdatingModalOpen;

    if (wasOpen && !isTeamUpdatingModalOpen && userId && TEAM_CREATE_TOURNAMENT_CALENDAR_ID) {
      queryClient.invalidateQueries({
        queryKey: getFetchTeamQueryKey(userId, TEAM_CREATE_TOURNAMENT_CALENDAR_ID),
      });
    }
  }, [isTeamUpdatingModalOpen, queryClient, userId, TEAM_CREATE_TOURNAMENT_CALENDAR_ID]);

  if (sessionState === 'resolving') {
    return <TeamPageLoadingSkeleton />;
  }

  if (sessionState === 'anonymous') {
    return <Navigate to="/" replace />;
  }

  if (!userId) {
    return <TeamPageLoadingSkeleton />;
  }

  if (
    !isViewMode &&
    isAuthenticated &&
    !existingTeamQuery.isPending &&
    !existingTeamQuery.isFetching &&
    !existingTeam
  ) {
    return <Navigate to="/" replace />;
  }

  if (!hasBootstrapped) {
    return <TeamPageLoadingSkeleton />;
  }

  if (!players.length) {
    return <TeamPageLoadingSkeleton />;
  }

  if (isViewMode) {
    if (viewedTeamQuery.isError || (!viewedTeamQuery.isLoading && !viewedTeamQuery.data)) {
      // Error / unavailable team state: show placeholder with error message
      return (
        <Suspense fallback={<TeamPageLoadingSkeleton />}>
          <ViewedTeamContainer
            teamId={viewedTeamId ?? ''}
            teamData={{
              id: viewedTeamId ?? '',
              teamName: 'Team Unavailable',
              managerName: 'Manager Unavailable',
              userId: null,
              tournamentCalendarId: null,
              budgetRemaining: null,
              transfersUsed: null,
              captainFantasyPlayerId: null,
              viceCaptainFantasyPlayerId: null,
              formation: null,
              createdAt: '',
              updatedAt: '',
              players: [],
            }}
            isPlaceholder={true}
            gameweekMeta={
              viewedGameweekId
                ? {
                    id: viewedGameweekId,
                    number: selectedGameweekMeta?.number ?? publicCurrentGameweek?.number,
                    status: selectedGameweekMeta?.status ?? publicCurrentGameweek?.status,
                    locksAt: selectedGameweekMeta?.startsAt ?? publicCurrentGameweek?.locksAt,
                  }
                : undefined
            }
            isUnavailable={true}
          />
        </Suspense>
      );
    }

    if (viewedTeamQuery.data && !viewedTeamQuery.isLoading) {
      const viewedTeam = viewedTeamQuery.data;
      const isPlaceholder = !viewedGameweekId || !isSelectedGameweekLocked;

      return (
        <Suspense fallback={<TeamPageLoadingSkeleton />}>
          <ViewedTeamContainer
            teamId={viewedTeamId ?? ''}
            teamData={viewedTeam}
            isPlaceholder={isPlaceholder}
            gameweekMeta={
              viewedGameweekId
                ? {
                    id: viewedGameweekId,
                    number: selectedGameweekMeta?.number ?? publicCurrentGameweek?.number,
                    status: selectedGameweekMeta?.status ?? publicCurrentGameweek?.status,
                    locksAt: selectedGameweekMeta?.startsAt ?? publicCurrentGameweek?.locksAt,
                  }
                : undefined
            }
          />
        </Suspense>
      );
    }

    // Loading state while fetching viewed team data
    return <TeamPageLoadingSkeleton />;
  }

  if (!existingTeam) {
    return <TeamPageLoadingSkeleton />;
  }

  const sharedProps = {
    currentMatchdayId: displayMatchdayMeta?.id ?? existingTeam.currentMatchday?.id,
    currentMatchdayNumber:
      displayMatchdayMeta?.number ?? existingTeam.currentMatchday?.matchdayNumber,
    currentMatchdayStatus:
      (displayMatchdayMeta?.status as typeof effectiveCurrentMatchdayStatus) ??
      effectiveCurrentMatchdayStatus,
    isCurrentMatchdayLockedForTeam,
    currentMatchdayPoints: existingTeam.currentMatchdayPoints,
    totalPoints: existingTeam.totalPoints,
    currentMatchdaySubInPlayerId: existingTeam.currentMatchdaySubstitution?.subInFantasyPlayerId,
    currentMatchdaySubOutPlayerId: existingTeam.currentMatchdaySubstitution?.subOutFantasyPlayerId,
    currentMatchdaySubstitution: existingTeam.currentMatchdaySubstitution,
    transferMatchdayId,
    transferMode,
    pendingTransfers: existingTeam.pendingTransfers,
    pendingCaptaincyChanges: existingTeam.pendingCaptaincyChanges,
    currentMatchdayMeta: displayMatchdayMeta ?? undefined,
    nextMatchdayMeta: publicNextMatchday ?? undefined,
    currentGameweekMeta: displayGameweekMeta ?? undefined,
    lockedGameweekId: publicCurrentGameweek?.id ?? undefined,
    lockedGameweekLocksAt: publicCurrentGameweek?.locksAt ?? undefined,
    nextGameweekMeta: publicNextGameweek ?? undefined,
    currentGameweekStatus,
    tabs,
    showTabs: shouldShowTabs,
    hasUnlimitedTransfers: existingTeam.hasUnlimitedTransfers,
    managedTransferSummary: existingTeam.managedTransferSummary,
    isGameweekFormat,
    isLastGameweek,
    currentTeamHydrationSource: existingTeam,
  } as const;

  return (
    <>
      <Suspense fallback={<TeamPageLoadingSkeleton />}>
        {shouldUseManageContainer ? (
          <ManageTeamContainer {...sharedProps} />
        ) : (
          <CurrentMatchdayContainer {...sharedProps} />
        )}
      </Suspense>

      <TeamUpdatingModal
        open={isTeamUpdatingModalOpen}
        onOpenChange={open => {
          if (!open) {
            setDismissedUpdatingWindowKey(updatingWindow?.key ?? null);
          }
        }}
        onViewLeagues={() => navigate('/leagues')}
        onViewMatchCentre={() => navigate('/match-centre')}
      />
    </>
  );
};
